import { useEffect, useRef, useState } from 'react'
import { Check, Pencil, X } from 'lucide-react'
import { cleanText, renderInlineEmphasis } from '../../QuickBooksCfoPresentationTab'

// Inline click-to-edit prose used across the studio preview so a preparer can
// rewrite the executive summary, a section summary, or a custom text block's
// body right where it renders — closing the gap where section summaries were
// AI-only and custom text bodies had no editor. Read-only viewers (and
// published snapshots) just see the rendered prose.

type EditableProseProps = {
  value: string
  canEdit: boolean
  onSave: (next: string) => void | Promise<void>
  placeholder?: string
  ariaLabel?: string
  testId?: string
}

export function EditableProse({
  value,
  canEdit,
  onSave,
  placeholder = 'Not configured',
  ariaLabel = 'commentary',
  testId,
}: EditableProseProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [editing, value])

  useEffect(() => {
    if (editing && textareaRef.current) {
      const el = textareaRef.current
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
  }, [editing])

  if (editing) {
    return (
      <div className="cfo-studio-edit" data-testid={testId ? `${testId}-edit` : undefined}>
        <textarea
          ref={textareaRef}
          rows={Math.min(10, Math.max(3, Math.ceil((draft.length || 1) / 64)))}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              setEditing(false)
            }
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault()
              void onSave(cleanText(draft))
              setEditing(false)
            }
          }}
          aria-label={`Edit ${ariaLabel}`}
        />
        <div className="cfo-studio-edit-actions">
          <button
            type="button"
            className="cfo-studio-edit-save"
            onClick={() => {
              void onSave(cleanText(draft))
              setEditing(false)
            }}
          >
            <Check size={13} /> Save
          </button>
          <button type="button" className="cfo-studio-edit-cancel" onClick={() => setEditing(false)}>
            <X size={13} /> Cancel
          </button>
          <span className="cfo-studio-edit-hint">⌘↵ to save</span>
        </div>
      </div>
    )
  }

  const text = cleanText(value)
  return (
    <div
      className="cfo-studio-prose"
      data-editor-empty={!text ? 'true' : undefined}
      data-testid={testId}
    >
      <p>{text ? renderInlineEmphasis(text) : placeholder}</p>
      {canEdit ? (
        <button
          type="button"
          className="cfo-studio-prose-edit"
          onClick={() => setEditing(true)}
          aria-label={`Edit ${ariaLabel}`}
          title={`Edit ${ariaLabel}`}
        >
          <Pencil size={13} />
        </button>
      ) : null}
    </div>
  )
}

// Inline editor for the alert / priority / data-caveat lists (one item per
// line). Falls back to a plain rendered list for read-only viewers.
type EditableListProps = {
  heading: string
  items: string[]
  extra?: string[]
  canEdit: boolean
  onSave: (next: string[]) => void | Promise<void>
}

export function EditableList({ heading, items, extra = [], canEdit, onSave }: EditableListProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(items.join('\n'))

  useEffect(() => {
    if (!editing) setDraft(items.join('\n'))
  }, [editing, items])

  if (editing) {
    return (
      <div className="cfo-studio-edit">
        <textarea
          rows={Math.max(3, items.length + 1)}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-label={`Edit ${heading}`}
          placeholder="One item per line"
        />
        <div className="cfo-studio-edit-actions">
          <button
            type="button"
            className="cfo-studio-edit-save"
            onClick={() => {
              void onSave(draft.split('\n').map((line) => line.trim()).filter(Boolean))
              setEditing(false)
            }}
          >
            <Check size={13} /> Save
          </button>
          <button type="button" className="cfo-studio-edit-cancel" onClick={() => setEditing(false)}>
            <X size={13} /> Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="cfo-studio-list">
      <div className="cfo-studio-list-head">
        <h2>{heading}</h2>
        {canEdit ? (
          <button type="button" className="cfo-studio-prose-edit" onClick={() => setEditing(true)} aria-label={`Edit ${heading}`}>
            <Pencil size={13} />
          </button>
        ) : null}
      </div>
      {items.length ? items.map((item, index) => <p key={`${item}-${index}`}>{renderInlineEmphasis(item)}</p>) : (canEdit ? <p className="cfo-studio-muted">Not configured</p> : null)}
      {extra.map((item, index) => <p key={`extra-${item}-${index}`}>{renderInlineEmphasis(item)}</p>)}
    </div>
  )
}
